// Use tsx file so tailwind picks up the class names
import { useToast } from "@smart/bridge-intake-components-dom";
import { IconName } from "@smart/itops-sb-design-system-dom";
import {
  convertToEmailHtml,
  convertToEmailText,
  convertToText,
  PlaceholderData,
  removeMarkdown,
} from "@smart/itops-serialisation-basic";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";

export const communicationMethodValue = [
  "communicate",
  "email",
  "internalUse",
] as const;

export type CommunicationMethodType = (typeof communicationMethodValue)[number];

export const communicationMethodLabel: Record<CommunicationMethodType, string> =
  {
    email: "Email",
    communicate: "Client Portal",
    internalUse: "Internal use",
  };

export const communicationMethodIcon: Partial<
  Record<
    CommunicationMethodType,
    {
      name: IconName;
      className?: string;
      mask?: Exclude<IconName, "archie">;
    }
  >
> = {
  email: { name: "solidEnvelope" },
  communicate: {
    name: "solidCommentCheck",
    className: "text-white bg-gradient-to-tr from-[#BA17CC] to-[#AA44BB]",
    mask: "solidSquareFull",
  },
};

export const useSendMessage = () => {
  const { communicate, correspondence, host, hostType } = useSmokeballApp();
  const { setToasts } = useToast();

  return {
    sendMessage: ({
      id,
      email,
      communicationMethod,
      subject,
      template,
      placeholderData,
      actionText,
      showToast = true,
      matterId,
    }: {
      id: string;
      email: string;
      communicationMethod: "email" | "communicate";
      subject: string;
      template: string;
      placeholderData: PlaceholderData;
      actionText?: string;
      showToast?: boolean;
      matterId?: string;
    }) => {
      switch (communicationMethod) {
        case "email": {
          correspondence?.email({
            subject,
            to: [{ emailAddress: email }],
            matterId,
            bodyHtml: convertToEmailHtml({ template, data: placeholderData }),
            bodyText: convertToEmailText({
              template,
              prettyDisplayText: true,
              data: placeholderData,
            }),
          });
          break;
        }
        case "communicate": {
          const messageRequest = {
            message: removeMarkdown(
              convertToText({ template, data: placeholderData }),
              ["links"],
            ),
            contactIds: [id],
            matterId,
          };
          if (
            hostType === "windows" &&
            host?.isEarlierThanVersion({ major: 8, minor: 10 })
          ) {
            communicate?.createMessage(messageRequest);
          } else {
            communicate?.sendMessage(messageRequest);
          }
          if (showToast) {
            setToasts([
              {
                text: `${actionText || "Form successfully shared"} via ${
                  communicationMethodLabel.communicate
                }`,
              },
            ]);
          }
          break;
        }
        default:
          break;
      }
    },
  };
};
